import React, { useState, useEffect, useMemo } from "react"
import classnames from "classnames"
import * as uuid from "uuid"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as sessions from "sessions"
import * as brands from "brands"
import * as api from "brandguard/api"
import * as predictions from "brandguard/predictions"
import * as bglayouts from "brandguard/components/layouts"
import * as bgapprovals from "brandguard/display/approval"
import * as bgimages from "brandguard/display/brandguard/images"
import * as bgtext from "brandguard/display/brandguard/text"
import * as bgdocuments from "brandguard/display/brandguard/documents"
import * as bgvideos from "brandguard/display/brandguard/videos"
import * as unified from "brandguard/display/unified"
import { UploadSimple } from "brandguard/components"
import * as authz from "authz"
import * as nav from "brandguard/components/navigation"
import * as debugx from "x/debugx"
import * as icons from "icons"
import * as bgscores from "brandguard/display/scores"
import * as typography from "typography"
import { FeedbackPanel, FeedbackHistory } from "brandguard/feedback"
import { awaitRequest } from "./awaiting"
import { sameMedia, singleEventType, singleUploader, CardScore, downloadUnified, tombstoneUnified, defaultLimit, applyOverride, FilterOverride, UnifiedFilterButton, UploadButton } from "./unified.helpers"

const defaultApproved = [api.Prediction.APPROVED, api.Prediction.INREVIEW, api.Prediction.REJECTED]

export default function Display(): JSX.Element {
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const permission = authzaccount.current
	const bearertoken = sessions.useToken()
	const brand = brands.caching.useCached()
	const profile = sessions.useProfile()
	const mtoggle = layouts.modals.useToggle()
	const { selected, setSelected, types } = unified.useAssetTypesFilter()

	const [loading, setLoading] = useState(true)
	const [selectedItem, setSelectedItem] = useState<unified.UnifiedItem | undefined>(undefined)
	const [checkedItems, setCheckedItems] = useState<unified.UnifiedItem[]>([])
	const [feedbackOpen, setFeedbackOpen] = useState(false)
	const [uploadOpen, setUploadOpen] = useState(false)
	const [filterFocused, setFilterFocused] = useState(false)
	const [undoItems, setUndoItems] = useState<unified.UnifiedItem[]>([])
	const [currentPage, setCurrentPage] = useState<api.DocumentPage | undefined>(undefined)

	const checkedMediaIds = useMemo(() => {
		const ids = new Set<string>()
		for (const c of checkedItems) if (c.data.media?.id) ids.add(c.data.media.id)
		return ids
	}, [checkedItems])

	const undoByMediaId = useMemo(() => {
		const m = new Map<string, unified.UnifiedItem>()
		for (const u of undoItems) if (u.data.media?.id) m.set(u.data.media.id, u)
		return m
	}, [undoItems])

	const _baseline: unified.FanoutRequests = useMemo(
		() => ({
			image: api.images.uploads.searches.zero({
				offset: 0n,
				limit: defaultLimit,
				brand_id: brand.id,
				queues: [uuid.NIL, brand.id],
				approved: defaultApproved,
			}),
			text: api.text.uploads.searchZero({
				offset: 0n,
				limit: defaultLimit,
				brand_id: brand.id,
				queues: [uuid.NIL, brand.id],
				approved: defaultApproved,
			}),
			document: api.documents.uploads.searches.zero({
				offset: 0n,
				limit: defaultLimit,
				brand_id: brand.id,
				queues: [uuid.NIL, brand.id],
				approved: defaultApproved,
			}),
			video: api.videos.uploads.searches.zero({
				offset: 0n,
				limit: defaultLimit,
				brand_id: brand.id,
				queues: [uuid.NIL, brand.id],
				approved: defaultApproved,
			}),
		}),
		[brand.id],
	)

	const [override, setOverride] = useState<Partial<FilterOverride>>({})
	const _requests: unified.FanoutRequests = useMemo(
		() => ({
			image: applyOverride(_baseline.image, override),
			text: applyOverride(_baseline.text, override),
			document: applyOverride(_baseline.document, override),
			video: applyOverride(_baseline.video, override),
		}),
		[_baseline, override],
	)

	const [requests, pagekey, setPage] = unified.useFanoutPage(_requests, types)

	const [result, setResult] = useState<unified.FanoutResult>({ items: [] })

	useEffect(() => {
		if (brand.id === uuid.NIL) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => unified.fanoutSearch(brand.id, types, requests, unified.uploadsSearchers, bearertoken))
			.then((r) => {
				setResult(r)
				setLoading(false)
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => {
				console.error("unable to search unified uploads", cause)
				setLoading(false)
			})
		return p.cancel
	}, [brand.id, types.join(","), requests])

	useEffect(() => {
		if (selectedItem?.event_type !== "document") {
			setCurrentPage(undefined)
			return
		}
		setCurrentPage(selectedItem.data.pages?.[0])
	}, [selectedItem?.event_type === "document" ? selectedItem.data.media?.id : undefined])

	const replaceItem = (updated: unified.UnifiedItem): void => {
		setResult((prev) => ({
			...prev,
			items: prev.items.map((i) => (sameMedia(i, updated) ? updated : i)),
		}))
		if (sameMedia(updated, selectedItem)) setSelectedItem(updated)
	}

	const removeItems = (ids: string[]): void => {
		setResult((prev) => ({ ...prev, items: prev.items.filter((i) => !ids.includes(i.data.media?.id ?? "")) }))
		setCheckedItems((prev) => prev.filter((i) => !ids.includes(i.data.media?.id ?? "")))
	}

	const prependItem = (added: unified.UnifiedItem): void => {
		setResult((prev) => ({ ...prev, items: [added, ...prev.items] }))
	}

	const agree = async (item: unified.UnifiedItem): Promise<unified.UnifiedItem> => {
		const bearer = authzc.bearer(authzaccount)
		const next = await (async (): Promise<unified.UnifiedItem> => {
			switch (item.event_type) {
				case "image": {
					const r = await api.images.approval.disagree(item.data as api.ImageSearchResponseItem & { event: api.ImageEvent }, bearer)
					return { event_type: "image", data: r }
				}
				case "text": {
					const r = await api.text.approval.disagree(item.data as api.TextSearchResponseItem & { event: api.TextEvent }, bearer)
					return { event_type: "text", data: r }
				}
				case "document": {
					const d = item.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent }
					const r = await api.documents.uploads.review(d.event.id, d.event.brand_id, bearer)
					return { event_type: "document", data: { ...r, pages: d.pages } }
				}
				case "video": {
					const d = item.data as api.VideoSearchResponseItem & { event: api.VideoEvent }
					const r = await api.videos.uploads.patch(
						d.event.brand_id,
						d.event.id,
						{ ...d, event: { ...d.event, observation: api.Prediction.INREVIEW } },
						bearer,
					)
					return { event_type: "video", data: r }
				}
			}
		})()
		setUndoItems((u) => [...u, item])
		replaceItem(next)
		return next
	}

	const disagree = async (item: unified.UnifiedItem): Promise<unified.UnifiedItem> => {
		const bearer = authzc.bearer(authzaccount)
		const tomb = { tombstoned_at: new Date().toISOString() }
		const obs = { observation: api.Prediction.DISCARDED_BY_UPLOADER }
		const next = await (async (): Promise<unified.UnifiedItem> => {
			switch (item.event_type) {
				case "image": {
					const d = item.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
					const r = await api.images.uploads.patch(
						d.event.brand_id,
						d.event.id,
						{ ...d, media: { ...d.media!, ...tomb }, event: { ...d.event, ...obs } },
						bearer,
					)
					return { event_type: "image", data: r }
				}
				case "text": {
					const d = item.data as api.TextSearchResponseItem & { event: api.TextEvent }
					const r = await api.text.uploads.patch(
						d.event.brand_id,
						d.event.id,
						{ ...d, media: { ...d.media!, ...tomb }, event: { ...d.event, ...obs } },
						bearer,
					)
					return { event_type: "text", data: r }
				}
				case "document": {
					const d = item.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent }
					const r = await api.documents.uploads.patch(
						d.event.brand_id,
						d.event.id,
						{ ...d, media: { ...d.media!, ...tomb }, event: { ...d.event, ...obs } },
						bearer,
					)
					return { event_type: "document", data: { ...r, pages: d.pages } }
				}
				case "video": {
					const d = item.data as api.VideoSearchResponseItem & { event: api.VideoEvent }
					const r = await api.videos.uploads.patch(
						d.event.brand_id,
						d.event.id,
						{ ...d, media: { ...d.media!, ...tomb }, event: { ...d.event, ...obs } },
						bearer,
					)
					return { event_type: "video", data: r }
				}
			}
		})()
		setUndoItems((u) => [...u, item])
		replaceItem(next)
		return next
	}

	const undo = async (current: unified.UnifiedItem, previously: unified.UnifiedItem): Promise<unknown> => {
		const bearer = authzc.bearer(authzaccount)
		const clearUndo = (): void =>
			setUndoItems((u) => u.filter((i) => i.data.media?.id !== previously.data.media?.id))
		switch (previously.event_type) {
			case "image": {
				const prev = previously.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
				const cur = current.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
				const resp = await api.images.uploads.patch(prev.event.brand_id, prev.event.id, prev, bearer)
				clearUndo()
				replaceItem({ event_type: "image", data: prev })
				return api.images.approvallog
					.find(cur.event.id, cur.event.brand_id, bearertoken)
					.then((r) => api.images.approvallog.destroy(r.event!.brand_id, r.event!.id, bearer).then(() => resp))
					.catch(httpx.errors.notFound(() => resp))
			}
			case "text": {
				const prev = previously.data as api.TextSearchResponseItem & { event: api.TextEvent }
				const cur = current.data as api.TextSearchResponseItem & { event: api.TextEvent }
				const resp = await api.text.uploads.patch(prev.event.id, prev.event.brand_id, prev, bearer)
				clearUndo()
				replaceItem({ event_type: "text", data: prev })
				return api.text.approvallog
					.find(cur.event.id, cur.event.brand_id, bearertoken)
					.then((r) => api.text.approvallog.destroy(r.event!.brand_id, r.event!.id, bearer).then(() => resp))
					.catch(httpx.errors.notFound(() => resp))
			}
			case "document": {
				const prev = previously.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent }
				const resp = await api.documents.uploads.patch(prev.event.brand_id, prev.event.id, prev, bearer)
				clearUndo()
				replaceItem({ event_type: "document", data: prev })
				return resp
			}
			case "video": {
				const prev = previously.data as api.VideoSearchResponseItem & { event: api.VideoEvent }
				const resp = await api.videos.uploads.patch(prev.event.brand_id, prev.event.id, prev, bearer)
				clearUndo()
				replaceItem({ event_type: "video", data: prev })
				return resp
			}
		}
	}

	// unapprove returns an auto approved asset to awaiting a review request.
	const unapprove = (item: unified.UnifiedItem): Promise<unknown> =>
		awaitRequest(item.event_type, item.data, authzc.bearer(authzaccount)).then((data) => {
			setUndoItems((u) => u.filter((i) => i.data.media?.id !== item.data.media?.id))
			replaceItem({ event_type: item.event_type, data } as unified.UnifiedItem)
		})

	useEffect(() => {
		if (!selectedItem?.data.event) return
		const event = selectedItem.data.event
		mtoggle(
			<layouts.Flex styled flexDirection="column">
				{renderPreview({
					item: selectedItem,
					currentPage,
					undoByMediaId,
					canUpload: permission.brandguard_upload,
					onClose: () => {
						mtoggle(undefined)
						setSelectedItem(undefined)
					},
					onPageChange: (_i, p) => setCurrentPage(p),
					agree,
					disagree,
					undo,
					unapprove: permission.brandguard_upload ? unapprove : undefined,
					onFeedback: (
						<>
							<layouts.containers.flex py="10px">
								<FeedbackPanel eventType={selectedItem.event_type} eventIds={[event.id]} uploadedBy={selectedItem.data.media?.uploaded_by} />
							</layouts.containers.flex>
							<layouts.containers.flex py="10px">
								<FeedbackHistory eventType={selectedItem.event_type} eventId={event.id} />
							</layouts.containers.flex>
						</>
					),
					onDelete: (ids) => {
						removeItems(ids)
						mtoggle(undefined)
						setSelectedItem(undefined)
					},
				})}
			</layouts.Flex>,
		).then(() => setSelectedItem(undefined))
	}, [selectedItem, currentPage, undoByMediaId])

	const allselected = result.items.length > 0 && checkedItems.length === result.items.length

	const handleSelectAll = (): void => {
		if (allselected) {
			setCheckedItems([])
			return
		}
		setCheckedItems(result.items)
	}

	const feedbackType = singleEventType(checkedItems)
	const feedbackEventIds = checkedItems.filter((i) => i.data.event).map((i) => i.data.event!.id)
	const feedbackUploader = singleUploader(checkedItems)

	return (
		<layouts.containers.flex flexDirection="column" overflow="hidden" flex="1">
			<layouts.containers.flex flexDirection="column-reverse" overflow="hidden" flex="1">
				<layouts.pagination.Cursor
					key={pagekey}
					my="auto"
					justifyContent="center"
					current={requests}
					advance={loading ? undefined : result.next}
					onChange={setPage}
				/>
				<layouts.loading.screen
					className="brandguard-working-area"
					loading={loading}
					flex="1"
					overflow="hidden"
					icon={<></>}
					flexDirection="row"
				>
					<layouts.containers.flex className="center-panel" flex="6" flexDirection="column">
						<layouts.containers.flex flexDirection="column" flex="1" minHeight="100px">
							<bglayouts.OverlaysContainer overflow="auto" background={layouts.theme.backgrounds.whitealpha80}>
								<layouts.containers.ResponsiveGrid gap={20} p="25px">
									{result.items.map((item) => (
										<UnifiedCard
											key={item.data.media?.id}
											item={item}
											active={sameMedia(item, selectedItem)}
											checked={checkedMediaIds.has(item.data.media?.id ?? "")}
											onCardClick={() => setSelectedItem(item)}
											onChecked={(state) =>
												setCheckedItems((prev) =>
													state ? [...prev, item] : prev.filter((c) => !sameMedia(c, item)),
												)
											}
											onChange={replaceItem}
											panel={
												<bgapprovals.status.default>
													{item.event_type === "video" ? (
														<bgapprovals.status.VideoUploads
															item={item.data as api.VideoSearchResponseItem}
															previtemstate={undoByMediaId.get(item.data.media?.id ?? "")?.data as api.VideoSearchResponseItem | undefined}
															agree={(v) =>
																agree({ event_type: "video", data: v }).then(
																	(r) => r.data as api.VideoSearchResponseItem,
																)
															}
															disagree={(v) =>
																disagree({ event_type: "video", data: v }).then(
																	(r) => r.data as api.VideoSearchResponseItem,
																)
															}
															undo={(c, p) =>
																undo(
																	{ event_type: "video", data: c },
																	{ event_type: "video", data: p },
																) as Promise<api.VideoPatchResponse>
															}
															unapprove={
																permission.brandguard_upload
																	? (v) => unapprove({ event_type: "video", data: v })
																	: undefined
															}
															borderRadius="0 0 10px 10px"
														/>
													) : (
														<bgapprovals.status.Uploads
															item={item.data}
															previtemstate={undoByMediaId.get(item.data.media?.id ?? "")?.data}
															agree={(x) =>
																agree({ event_type: item.event_type, data: x } as unified.UnifiedItem).then(
																	(r) => r.data,
																)
															}
															disagree={(x) =>
																disagree({ event_type: item.event_type, data: x } as unified.UnifiedItem).then(
																	(r) => r.data,
																)
															}
															undo={(c, p) =>
																undo(
																	{ event_type: item.event_type, data: c } as unified.UnifiedItem,
																	{ event_type: item.event_type, data: p } as unified.UnifiedItem,
																) as Promise<typeof c>
															}
															unapprove={
																permission.brandguard_upload
																	? (x) => unapprove({ event_type: item.event_type, data: x } as unified.UnifiedItem)
																	: undefined
															}
															borderRadius="0 0 10px 10px"
														/>
													)}
												</bgapprovals.status.default>
											}
										/>
									))}
								</layouts.containers.ResponsiveGrid>
								<layouts.loading.pending loading={loading}>
									{result.items.length === 0 && <bgapprovals.layouts.ZeroState />}
								</layouts.loading.pending>
							</bglayouts.OverlaysContainer>
						</layouts.containers.flex>
					</layouts.containers.flex>
				</layouts.loading.screen>
				{feedbackOpen && feedbackType && feedbackEventIds.length > 0 && (
					<layouts.containers.flex mb="10px">
						<FeedbackPanel eventType={feedbackType} eventIds={feedbackEventIds} uploadedBy={feedbackUploader} />
					</layouts.containers.flex>
				)}
				<layouts.containers.flex flexDirection="row" alignItems="center" className={classnames(bgapprovals.layouts.styledactions)}>
					<UploadButton
						onToggle={() => {
							if (filterFocused) setFilterFocused(false)
							setUploadOpen(!uploadOpen)
						}}
						panel={
							uploadOpen ? (
								renderUploadPanel({
									brandId: brand.id,
									profileId: profile.id,
									bearer: authzc.bearer(authzaccount),
									bearertoken,
									canUpload: permission.brandguard_upload,
									onItemUploaded: prependItem,
									onClose: () => setUploadOpen(false),
								})
							) : (
								<></>
							)
						}
					/>
					<UnifiedFilterButton
						baseline={_baseline.image}
						current={_requests.image}
						focused={filterFocused}
						onToggle={() => {
							if (!filterFocused && uploadOpen) setUploadOpen(false)
							setFilterFocused(!filterFocused)
						}}
						onClear={() => setOverride({})}
						onChange={(next) => setOverride((prev) => ({ ...prev, ...next }))}
						showApprovedRejectedCheckboxes
						assetTypesSelected={selected}
						onAssetTypesChange={setSelected}
					/>
					<layouts.containers.flex className="actions" justifyContent="flex-end" flex="10">
						{permission.brandguard_review && (
							<bgapprovals.layouts.FeedbackToggle
								disabled={!feedbackType || feedbackEventIds.length === 0}
								onClick={() => setFeedbackOpen((v) => !v)}
							>
								{feedbackOpen ? "Hide Feedback" : "Give Feedback"}
							</bgapprovals.layouts.FeedbackToggle>
						)}
						<bgapprovals.layouts.SelectAll
							disabled={result.items.length === 0}
							cursor="pointer"
							className="select-all-action"
							onClick={handleSelectAll}
						>
							{allselected ? "Unselect All" : "Select All"}
						</bgapprovals.layouts.SelectAll>
						<authz.Protected
							enabled={permission.brandguard_upload && checkedItems.length > 0}
							rejected={
								<>
									<layouts.containers.flex className="download-action">
										<icons.brandguard.download />
									</layouts.containers.flex>
									<layouts.containers.flex className="remove-action">
										<icons.brandguard.remove />
									</layouts.containers.flex>
								</>
							}
						>
							<layouts.containers.flex
								className="download-action"
								cursor="pointer"
								onClick={() =>
									downloadUnified({ items: checkedItems, accountID: authzaccount.current.id, bearertoken })
								}
							>
								<icons.brandguard.download />
							</layouts.containers.flex>
							<layouts.containers.flex
								className="remove-action"
								cursor="pointer"
								onClick={() =>
									tombstoneUnified({ items: checkedItems, bearer: authzc.bearer(authzaccount) })
										.then((ids) => removeItems(ids))
										.catch((cause) => console.error("unable to tombstone unified uploads", cause))
								}
							>
								<icons.brandguard.remove />
							</layouts.containers.flex>
						</authz.Protected>
					</layouts.containers.flex>
				</layouts.containers.flex>
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}

interface unifiedCardProps {
	item: unified.UnifiedItem
	active: boolean
	checked: boolean
	panel: JSX.Element
	onCardClick(): void
	onChecked(state: boolean): void
	onChange(next: unified.UnifiedItem): void
}

function UnifiedCard(props: unifiedCardProps): JSX.Element {
	const { item, active, checked, panel, onCardClick, onChecked, onChange } = props
	const score = <CardScore item={item} />
	switch (item.event_type) {
		case "image":
			return (
				<bgapprovals.images.CardDisplay
					item={item.data}
					active={active}
					checked={checked}
					score={score}
					panel={panel}
					onCardClick={onCardClick}
					onChecked={onChecked}
					onChange={(v) => onChange({ event_type: "image", data: v })}
					find={api.images.uploads.find}
				/>
			)
		case "text":
			return (
				<bgapprovals.texts.CardDisplay
					item={item.data}
					active={active}
					checked={checked}
					score={score}
					panel={panel}
					onCardClick={onCardClick}
					onChecked={onChecked}
					onChange={(v) => onChange({ event_type: "text", data: v })}
					find={api.text.uploads.find}
				/>
			)
		case "document":
			return (
				<bgapprovals.documents.CardDisplay
					item={item.data}
					active={active}
					checked={checked}
					score={score}
					panel={panel}
					onCardClick={onCardClick}
					onChecked={onChecked}
					onChange={(v) => onChange({ event_type: "document", data: v })}
					find={api.documents.uploads.find}
				/>
			)
		case "video":
			return (
				<bgapprovals.videos.CardDisplay
					item={item.data}
					active={active}
					checked={checked}
					score={score}
					panel={panel}
					onCardClick={onCardClick}
					onChecked={onChecked}
					onChange={(v) => onChange({ event_type: "video", data: v })}
					find={api.videos.uploads.find}
				/>
			)
	}
}

interface previewProps {
	item: unified.UnifiedItem
	currentPage: api.DocumentPage | undefined
	undoByMediaId: Map<string, unified.UnifiedItem>
	canUpload: boolean
	onClose(): void
	onPageChange(index: number, page: api.DocumentPage | undefined): void
	agree(item: unified.UnifiedItem): Promise<unified.UnifiedItem>
	disagree(item: unified.UnifiedItem): Promise<unified.UnifiedItem>
	undo(current: unified.UnifiedItem, previously: unified.UnifiedItem): Promise<unknown>
	unapprove?(item: unified.UnifiedItem): Promise<unknown>
	onFeedback: JSX.Element
	onDelete(ids: string[]): void
}

function renderPreview(props: previewProps): JSX.Element {
	const { item, currentPage, undoByMediaId, canUpload, onClose, onPageChange, agree, disagree, undo, unapprove, onFeedback, onDelete } =
		props
	const previtem = undoByMediaId.get(item.data.media?.id ?? "")
	const scoresPanel = (() => {
		switch (item.event_type) {
			case "image":
				return <bgimages.Scores item={item.data} />
			case "text":
				return <bgtext.Scores item={item.data} />
			case "document":
				return <bgdocuments.Scores item={item.data} currentPage={currentPage} />
			case "video":
				return <bgvideos.Scores item={item.data} />
		}
	})()
	const debugPanel = (() => {
		switch (item.event_type) {
			case "image":
				return <bgimages.Debug item={item.data} />
			case "text":
				return <bgtext.Debug item={item.data} />
			case "document":
				return <bgdocuments.Debug item={item.data} />
			case "video":
				return (
					<layouts.containers.flex flexDirection="column" fontSize="12px">
						<layouts.containers.flex>ID: {item.data.event?.id}</layouts.containers.flex>
						<layouts.containers.flex>Confidence: {item.data.event?.confidence}</layouts.containers.flex>
						<layouts.containers.flex>Brand Voice: {item.data.event?.video_brand_voice}</layouts.containers.flex>
						<layouts.containers.flex>Violence: {item.data.event?.video_violence}</layouts.containers.flex>
						<layouts.containers.flex>Nudity: {item.data.event?.video_sexually_explicit}</layouts.containers.flex>
					</layouts.containers.flex>
				)
		}
	})()
	const statusRow =
		item.event_type === "video" ? (
			<bgapprovals.status.VideoUploads
				item={item.data as api.VideoSearchResponseItem}
				previtemstate={previtem?.data as api.VideoSearchResponseItem | undefined}
				agree={(v) => agree({ event_type: "video", data: v }).then((r) => r.data as api.VideoSearchResponseItem)}
				disagree={(v) => disagree({ event_type: "video", data: v }).then((r) => r.data as api.VideoSearchResponseItem)}
				undo={(c, p) =>
					undo({ event_type: "video", data: c }, { event_type: "video", data: p }) as Promise<api.VideoPatchResponse>
				}
				unapprove={unapprove && ((v) => unapprove({ event_type: "video", data: v }))}
				borderRadius="10px"
			/>
		) : (
			<bgapprovals.status.Uploads
				item={item.data}
				previtemstate={previtem?.data}
				agree={(x) => agree({ event_type: item.event_type, data: x } as unified.UnifiedItem).then((r) => r.data)}
				disagree={(x) => disagree({ event_type: item.event_type, data: x } as unified.UnifiedItem).then((r) => r.data)}
				undo={(c, p) =>
					undo(
						{ event_type: item.event_type, data: c } as unified.UnifiedItem,
						{ event_type: item.event_type, data: p } as unified.UnifiedItem,
					) as Promise<typeof c>
				}
				unapprove={unapprove && ((x) => unapprove({ event_type: item.event_type, data: x } as unified.UnifiedItem))}
				borderRadius="10px"
			/>
		)
	const multiActions = (() => {
		switch (item.event_type) {
			case "image":
				return (
					<bgimages.MultiActions
						uploadItems={[item.data]}
						deleteAction={canUpload ? api.images.uploads.patch : undefined}
						onDelete={(r) => onDelete(r.map((el) => el.media!.id))}
					/>
				)
			case "text":
				return (
					<bgtext.MultiActions
						uploadItems={[item.data]}
						deleteAction={canUpload ? api.text.uploads.patch : undefined}
						onDelete={(r) => onDelete(r.map((el) => el.media!.id))}
					/>
				)
			case "document":
				return (
					<bgdocuments.MultiActions
						uploadItems={[item.data]}
						historicalItems={[]}
						deleteAction={canUpload ? api.documents.uploads.patch : undefined}
						onDelete={(r) => onDelete(r.map((el) => el.media!.id))}
					/>
				)
			case "video":
				return (
					<bgvideos.MultiActions
						uploadItems={[item.data]}
						deleteAction={canUpload ? api.videos.uploads.patch : undefined}
						onDelete={(r) => onDelete(r.map((el) => el.media!.id))}
					/>
				)
		}
	})()
	const body = (
		<>
			<typography.h3 fontWeight="700" fontSize="18px" lineHeight="30px" color="#4D5E80">
				Asset Scores
			</typography.h3>
			{scoresPanel}
			<bgapprovals.status.default>{statusRow}</bgapprovals.status.default>
			{onFeedback}
			<authz.Protected enabled={debugx.alpha.enabled()}>
				<bgscores.layouts.accordion.Container title="Debug Info" textAlign="initial">
					{debugPanel}
				</bgscores.layouts.accordion.Container>
			</authz.Protected>
			<layouts.containers.flex className="actions" flex="1" alignItems="flex-end">
				<authz.Protected enabled={canUpload}>{multiActions}</authz.Protected>
			</layouts.containers.flex>
		</>
	)
	switch (item.event_type) {
		case "image":
			return (
				<bgapprovals.ImagePreview item={item.data} flex="1" onClose={onClose}>
					{body}
				</bgapprovals.ImagePreview>
			)
		case "text":
			return (
				<bgapprovals.TextPreview item={item.data} flex="1" onClose={onClose}>
					{body}
				</bgapprovals.TextPreview>
			)
		case "document":
			return (
				<bgapprovals.DocumentPreview item={item.data} flex="1" onClose={onClose} onPageChange={onPageChange}>
					{body}
				</bgapprovals.DocumentPreview>
			)
		case "video":
			return (
				<bgapprovals.VideoPreview item={item.data} flex="1" onClose={onClose}>
					{body}
				</bgapprovals.VideoPreview>
			)
	}
}

interface uploadPanelProps {
	brandId: string
	profileId: string
	bearer: httpx.option
	bearertoken: httpx.option
	canUpload: boolean
	onItemUploaded(item: unified.UnifiedItem): void
	onClose(): void
}

// renderUploadPanel docks the upload panel that accepts every asset type.
function renderUploadPanel(props: uploadPanelProps): JSX.Element {
	const { brandId, profileId, bearer, bearertoken, canUpload, onItemUploaded, onClose } = props
	const DropWell = (dpProps: inputs.uploads.api.Props): JSX.Element => {
		if (!canUpload) {
			return (
				<layouts.containers.flex flex="1" className={classnames(bglayouts.uploads.navdenied)}>
					<authz.layouts.UploadDenied className="styled-area">
						You do not have permissions to upload new assets for scoring. Please contact an admin.
					</authz.layouts.UploadDenied>
				</layouts.containers.flex>
			)
		}
		return <unified.Upload background={layouts.theme.colors.blue.blue} observation={api.Prediction.APPROVED} {...dpProps} />
	}
	return (
		<UploadSimple
			className={classnames(nav.layouts.slideDownAnimation)}
			DropWell={DropWell}
			onClose={onClose}
			isProcessing={(i: unified.UnifiedItem) => predictions.processing(i.data.event?.approved)}
			onChange={onItemUploaded}
			resume={() => unified.resumeUploads(brandId, profileId, bearertoken)}
			refresh={(i) => unified.refreshUpload(i, brandId, bearertoken)}
			upload={(_s, content) => unified.uploadFile(content, brandId, bearer)}
		/>
	)
}
